<template>
  <div>
    <emd-form-group
      :state="feedbacks.password.state"
      :label="$t('PASSWORD-LABEL')"
      labelFor="password"
    >
      <div class="emd-input-group">
        <emd-input
          name="password"
          v-model="value.password"
          class="fs-exclude"
          type="password"
          :minLength="8"
          required
          :onInput="validateInput"
          ref="passwordInput"
          :placeholder="$t('PASSWORD-PLACEHOLDER')"
          :data-cy="dataCyPassword"
          :disabled="disabled"
        />

        <a class="emd-input-icon__link emd-link _color_primary _align_right" href="javascript:void(0)" :tabindex="-1" @click="showPassword($refs.passwordInput)">
          <i class="uil uil-eye-slash" v-if="passwordVisibility.password"></i>
          <i class="uil uil-eye" v-else></i>
        </a>
      </div>
      <p
        :data-cy="feedback.dataCy"
        v-for="feedback in feedbacks.password.values.filter(fb => fb.condition)"
        :key="feedback.msg"
        class="emd-input-feedback"
        data-testid="form-group-feedback"
      >
        {{feedback.msg}}
      </p>
      <ul class="emd-input-feedback-password" v-if="validity">
        <li
          v-for="feedback in passwordRulesFeedbacks" :key="feedback.msg"
          class="emd-text _size_small"
          :class="{
            '_color_primary': feedback.condition,
            '_color_danger': !feedback.condition,
            '_color_warning':  !feedbacks.password.state && isEmpty(value.password)
          }">
          <p >{{ feedback.msg }}</p>
        </li>
      </ul>
    </emd-form-group>
    <emd-form-group
      :state="feedbacks.confirmPassword.state"
      :label="$t('PASSWORD-CONFIRM-LABEL')"
      labelFor="confirmPassword"
      :feedbacks="feedbacks.confirmPassword.values"
    >
        <div class="emd-input-group">
          <emd-input
            name="confirmPassword"
            v-model="value.confirmPassword"
            class="fs-exclude"
            type="password"
            :minLength="8"
            required
            :onInput="validateInput"
            ref="confirmPassword"
            :placeholder="$t('PASSWORD-PLACEHOLDER')"
            :data-cy="dataCyConfirmPassword"
            :disabled="disabled"
          />
          <a class="emd-input-icon__link emd-link _color_primary _align_right" href="javascript:void(0)" :tabindex="-1" @click="showPassword($refs.confirmPassword)">
            <i class="uil uil-eye-slash" v-if="passwordVisibility.confirmPassword"></i>
            <i class="uil uil-eye" v-else></i>
          </a>
        </div>
    </emd-form-group>
  </div>
</template>
<script>
import { EmdFormGroup, EmdInput } from '../../../index'
import { passwordRulesValidation, passwordValidation, togglePasswordVisibility, validatePasswordForm } from './helper'

export default {
  name: 'EmdNewPassword',
  components: {
    EmdFormGroup,
    EmdInput
  },
  data () {
    return {
      validity: {
        password: {
          valid: true
        },
        confirmPassword: {
          valid: true
        },
        passwordMismatch: false
      },
      feedbacks: {
        password: {
          state: '',
          values: []
        },
        confirmPassword: {
          state: '',
          values: []
        }
      },
      passwordVisibility: {
        password: false,
        confirmPassword: false
      }
    }
  },
  props: {
    value: {
      required: true,
      type: Object,
      validator: value => ['password', 'confirmPassword'].every(key => key in value)
    },
    startFocused: {
      required: false,
      type: Boolean,
      default: false
    },
    showErrorFromParent: {
      required: false,
      type: Boolean,
      default: false
    },
    dataCyPassword: {
      type: String,
      required: false,
      default: ''
    },
    dataCyConfirmPassword: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    passwordHasDirty () {
      return this.value.password !== undefined
    },
    passwordRulesFeedbacks () {
      if (this.validity) {
        const notAllowed = ['valueMissing', 'valid', 'maxLength']

        return Object.keys(this.validity.password)
          .filter(key => !notAllowed.includes(key))
          .map(key => {
            return {
              msg: this.$t(`REQUIRED-${key.toUpperCase()}`),
              key: key.toLowerCase(),
              condition: this.passwordHasDirty && this.validity.password[key]
            }
          })
      } else {
        return []
      }
    }
  },
  methods: {
    isEmpty (value) {
      return (/^\s*$/).test(value) || [undefined, null, ''].includes(value)
    },
    passwordFeedbacks (name) {
      const values = this.validity ? [
        {
          msg: this.$t('REQUIRED-TEXT'),
          condition: this.validity[name].valueMissing
        },
        {
          msg: this.$t('REQUIRED-MAXLENGTH'),
          condition: this.validity[name].maxLength
        },
        (name === 'confirmPassword') ? {
          msg: this.$t('PASSWORD-MATCH'),
          condition: !this.validity[name].valueMissing && this.validity.passwordMismatch
        } : {}
      ] : []

      const count = values && values.filter(feed => feed.condition).length
      const state = count === 0 && this.validity[name].valid
        ? 'success'
        : count && Object.keys(this.validity[name]).length > 1 ? 'error' : ''
      return {
        state,
        values
      }
    },
    passwordMismatch (password1, password2) { return password1 !== password2 || false },
    showPassword (input) { this.passwordVisibility[input.name] = togglePasswordVisibility(input) },
    validateInput (e) {
      this.validity[e.target.name] = passwordValidation(e.target)
      this.validity.passwordMismatch = this.passwordMismatch(this.value.password, this.value.confirmPassword)
      this.feedbacks[e.target.name] = this.passwordFeedbacks(e.target.name)
      if (e.target.name === 'password' && !this.validity.confirmPassword.valueMissing) {
        this.feedbacks.confirmPassword = this.passwordFeedbacks('confirmPassword')
      }
      this.value.valid = validatePasswordForm(this.validity)
    },
    reset () {
      this.$refs.passwordInput.$el.value = ''
      this.$refs.confirmPassword.$el.value = ''
      this.feedbacks = {
        password: {
          state: '',
          values: []
        },
        confirmPassword: {
          state: '',
          values: []
        }
      }
      this.value.password = ''
      this.validity = {
        password: passwordRulesValidation({ value: '' }),
        confirmPassword: passwordValidation(this.$refs.confirmPassword.$el),
        passwordMismatch: false
      }
    }
  },
  mounted () {
    this.validity.password = passwordRulesValidation({ value: this.passwordHasDirty ? this.value.password : '' })
    this.validity.confirmPassword = passwordValidation(this.$refs.confirmPassword.$el)
    if (this.startFocused) {
      this.$refs.passwordInput.$el.focus()
    }
  }
}
</script>
<i18n>
{
  "pt-BR": {
    "PASSWORD-LABEL": "Nova senha",
    "PASSWORD-CONFIRM-LABEL": "Confirme a nova senha",
    "PASSWORD-PLACEHOLDER": "Digite sua senha",
    "REQUIRED-TEXT": "Campo obrigatório",
    "REQUIRED-MAXLENGTH": "Máximo de 64 caracteres",
    "REQUIRED-MINLENGTH": "Mínimo de 8 caracteres",
    "REQUIRED-NUMBER": "Conter números",
    "REQUIRED-LOWER": "Conter letra minúscula",
    "REQUIRED-UPPER": "Conter letra maiúscula",
    "REQUIRED-SEQUENCE": "Não conter 3 ou mais caracteres em sequência (Ex.: 123, abc)",
    "REQUIRED-SPECIAL": "Conter caracteres especiais (Ex.: !, @, #, $, %)",
    "REQUIRED-REPEAT": "Não conter 3 ou mais caracteres iguais em sequência (Ex.: 111, aaa)",
    "PASSWORD-MATCH": "As senhas não são iguais",
    "PASSWORD-STR": "Sua senha não é forte o suficiente."
  },
  "en-US": {
    "PASSWORD-LABEL": "New Password",
    "PASSWORD-CONFIRM-LABEL": "Confirm New Password",
    "PASSWORD-PLACEHOLDER": "Type your password",
    "REQUIRED-TEXT": "This field is required",
    "REQUIRED-MAXLENGTH": "64 characters maximum",
    "REQUIRED-MINLENGTH": "Minimum of 8 characters",
    "REQUIRED-NUMBER": "Must contain number",
    "REQUIRED-LOWER": "Must contain lowercase letter",
    "REQUIRED-UPPER": "Must contain capital letter",
    "REQUIRED-SEQUENCE": "Not contain 3 or more characters in sequence (Ex.: 123, abc)",
    "REQUIRED-SPECIAL": "Must contain special character (EX.: !, @, #, $, %.)",
    "REQUIRED-REPEAT": "Not contain 3 or more identical characters in sequence (Ex.: 111, aaa)",
    "PASSWORD-MATCH": "Passwords are not the same",
    "PASSWORD-STR": "Password not strong enough"
  }
}
</i18n>
<style>
  .Password.emd-password {
    max-width: 100%;
    border-radius: 3px;
  }
  .emd-strength {
    position: relative;
    height: 3px;
    background: #DDD;
    margin: 2px auto 0px;
    border-radius: 3px;
  }
  ._mode_dark .emd-strength {
    background: #486375; /* black 400 */
  }
  ._mode_light .emd-strength {
    background: #B6C8D4; /* black 400 */
  }
  .emd-input-feedback-password {
    columns: 1;
    -webkit-columns: 1;
    -moz-columns: 1;
    list-style: disc;
    font-weight: 700;
    text-align: left;
    padding-left: 28px;
    padding-right: 6px;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  @media (min-width: 420px) {
    .emd-input-feedback-password {
      columns: 2;
      -webkit-columns: 2;
      -moz-columns: 2;
    }
  }

  .emd-input-feedback-password li{
    margin: unset;
    padding: unset;
  }
</style>
